import React, { useState, useEffect, useRef } from "react";
import {
  postMemoryTreePhoto,
  getMemoryTreeSingle,
  getCurrentPackageUserModule,
} from "../../../services/api";
import ModuleHeader from "../../moduleHeader";
import { useParams } from "react-router-dom";

export default function MemoryTreeInside({
  data,
  onBack,
  isExample = false,
  id,
}) {
  const { id: routeId } = useParams();
  const actualId = id || routeId;
  const [visibleItems, setVisibleItems] = useState([]);
  const [photos, setPhotos] = useState(data?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  const [packageData, setPackageData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // Paket verisini çek
  useEffect(() => {
    if (!actualId) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(actualId, 9); // MemoryTree moduleId: 9
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPackageData();
  }, [actualId]);

  useEffect(() => {
    setPhotos(data?.photos.reverse() || []);
  }, [data]);

  const timelineData = photos.map((p, idx) => ({
    id: p.uuid || p.id || idx,
    image:
      p.url || p.photo_url || p.image || "/assets/pngImages/visionBoard.png",
    description: p.description || p.title || "Anı",
    date: (p.created_at || data?.created_at || "").split(" ")[0],
  }));

  // Animasyon için öğeleri sırayla göster
  useEffect(() => {
    setVisibleItems([]);
    timelineData.forEach((_, index) => {
      setTimeout(() => {
        setVisibleItems((prev) => [...prev, index]);
      }, index * 300);
    });
  }, [photos]);

  const refreshPhotos = async () => {
    if (!data?.uuid) return;
    try {
      const response = await getMemoryTreeSingle(data.uuid);
      const fresh = response?.data.reverse();
      setPhotos(fresh?.photos || []);
    } catch (err) {
      console.error("Anı ağacı yeniden çekilirken hata", err);
    }
  };

  const TimelineItem = ({ item, index, isLeft }) => (
    <div
      className={`relative flex items-center justify-center mb-12 ${
        visibleItems.includes(index)
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4"
      } transition-all duration-500 ease-out`}
    >
      {/* Sol taraf container */}
      <div className="w-[160px] flex justify-end pr-4">
        {isLeft && (
          <div className="bg-white rounded-2xl p-3 border border-[#BBBBBB] w-full">
            <div className="aspect-square rounded-xl overflow-hidden mb-2">
              <img
                src={item.image}
                alt={item.description}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-gray-800 font-medium mb-1 text-start">
              {item.description}
            </p>
            <p className="text-xs text-gray-800 text-start">{item.date}</p>
          </div>
        )}
      </div>

      {/* Orta nokta - Mutlak konumlandırma */}
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
        <div
          className={`w-3 h-3 rounded-full border-2 border-[#3DA7B5] bg-white shadow-sm ${
            visibleItems.includes(index) ? "scale-100" : "scale-0"
          } transition-transform duration-300 delay-200`}
        />
      </div>

      {/* Sağ taraf container */}
      <div className="w-[160px] flex justify-start pl-4">
        {!isLeft && (
          <div className="bg-white rounded-2xl p-3 border border-[#BBBBBB] w-full">
            <div className="aspect-square rounded-xl overflow-hidden mb-2">
              <img
                src={item.image}
                alt={item.description}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-start text-gray-800 font-medium mb-1 ">
              {item.description}
            </p>
            <p className="text-xs text-start text-gray-800 ">{item.date}</p>
          </div>
        )}
      </div>
    </div>
  );

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !data?.uuid) return;
    try {
      setUploading(true);
      const response = await postMemoryTreePhoto(data.uuid, selectedFile, {
        title: title || undefined,
      });
      const uploaded = response?.data?.photo || response?.data || response;
      const newPhoto = {
        id: uploaded?.uuid || uploaded?.id || Date.now(),
        url: uploaded?.url || uploaded?.photo_url || uploaded?.image,
        description: uploaded?.description || uploaded?.title || title || "Anı",
        created_at: uploaded?.created_at || new Date().toISOString(),
      };
      setPhotos((prev) => [newPhoto, ...prev]);
      // form reset
      setTitle("");
      setSelectedFile(null);
      setPreviewUrl("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      // sunucudan taze veriyi çek
      await refreshPhotos();
    } catch (err) {
      console.error("Fotoğraf yükleme hatası", err);
    } finally {
      setUploading(false);
    }
  };

  const AddNewItem = ({ isLeft }) => (
    <div
      className={`relative flex items-center justify-center mb-12 ${"opacity-100 translate-y-0"} transition-all duration-500 ease-out delay-500`}
    >
      {/* Sol taraf container */}
      <div className="w-[160px] flex justify-end pr-4">
        {isLeft && (
          <form
            onSubmit={handleUpload}
            className="bg-[#f2f5f8] rounded-2xl border border-gray-200 p-3 min-h-[160px] flex flex-col items-stretch justify-center w-full gap-3"
          >
            <div
              onClick={() => fileInputRef.current?.click()}
              className="h-32 rounded-2xl bg-[#eef2f6] flex items-center justify-center cursor-pointer"
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="preview"
                  className="h-full w-full object-cover rounded-2xl"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <img
                    src="/assets/icons/galleryAdd.png"
                    alt="add"
                    className="w-6 h-6 object-cover"
                  />
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setSelectedFile(f);
                setPreviewUrl(f ? URL.createObjectURL(f) : "");
              }}
              className="hidden"
            />
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Yeni Anı Oluştur"
              className="text-sm rounded-xl border-0 bg-[#eef2f6] px-4 py-3 text-gray-700 placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={uploading || !selectedFile}
              className="mt-1 text-sm bg-white text-[#3DA7B5] rounded-xl py-2 border border-gray-200 disabled:opacity-60"
            >
              {uploading ? "Yükleniyor..." : "Anınızı Ekleyin"}
            </button>
          </form>
        )}
      </div>

      {/* Orta nokta - Mutlak konumlandırma */}
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
        <div className="w-3 h-3 rounded-full border-2 border-gray-300 bg-gray-100 shadow-sm" />
      </div>

      {/* Sağ taraf container */}
      <div className="w-[160px] flex justify-start pl-4">
        {!isLeft && (
          <form
            onSubmit={handleUpload}
            className="bg-[#f2f5f8] rounded-2xl border border-gray-200 p-3 min-h-[160px] flex flex-col items-stretch justify-center w-full gap-3"
          >
            <div
              onClick={() => fileInputRef.current?.click()}
              className="h-32 rounded-2xl bg-[#eef2f6] flex items-center justify-center cursor-pointer"
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="preview"
                  className="h-full w-full object-cover rounded-2xl"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#DADADA] flex items-center justify-center">
                  <img
                    src="/assets/icons/galleryAdd.png"
                    alt="add"
                    className="w-6 h-6 object-cover"
                  />
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setSelectedFile(f);
                setPreviewUrl(f ? URL.createObjectURL(f) : "");
              }}
              className="hidden"
            />
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Yeni Anı Oluştur"
              className="text-xs rounded-xl border-0 bg-[#eef2f6] px-4 py-3 text-gray-700 placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={uploading || !selectedFile}
              className="mt-1 font-semibold bg-white text-[#2F646C] text-xs rounded-xl py-2 border border-gray-200 disabled:opacity-60"
            >
              {uploading ? "Yükleniyor..." : "Anınızı Ekleyin"}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  // Loading screen
  if (pageLoading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle="Anı Ağacı"
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        <div className="w-full px-6 py-8">
          {/* Geri Dönüş Butonu */}
          {onBack && (
            <div className="mb-6">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-[#3DA7B5] text-sm font-medium hover:text-[#2F8894] transition-colors"
              >
                <span className="text-lg">←</span>
                Geri Dön
              </button>
            </div>
          )}

          {/* Örnek Anı Ağacı Uyarısı */}
          {isExample && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-lg">ℹ️</span>
                </div>
                <div>
                  <p className="text-blue-800 text-sm font-medium">
                    Örnek Anı Ağacı
                  </p>
                  <p className="text-blue-600 text-xs">
                    Bu bir örnek anı ağacıdır. Kendi anı ağacınızı oluşturmak
                    için geri dönün.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-2">
              <img
                className="w-14 h-14 rounded-full"
                src={
                  data?.users?.user?.profile_picture ||
                  "/assets/pngImages/user.png"
                }
                alt="memoryTree"
              />
              <span className="text-sm text-gray-800 font-semibold">
                {data?.users?.user?.name || "Kullanıcı"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <img
                className="w-14 h-14 rounded-full "
                src={
                  data?.users?.partner?.profile_picture ||
                  "/assets/pngImages/user.png"
                }
                alt="memoryTree"
              />
              <span className="text-sm text-gray-800 font-semibold">
                {data?.users?.partner?.name || "Partner"}
              </span>
            </div>
          </div>
          <div className="relative max-w-md mx-auto px-2">
            {/* Ana dikey çizgi */}
            <div className="absolute left-1/2 top-0 bottom-0 transform -translate-x-1/2 w-0.5 bg-gray-200 z-10" />

            {/* Timeline öğeleri */}
            <div className="relative z-20">
              {/* Yeni ekleme alanı her zaman en üstte */}
              <AddNewItem isLeft={false} />

              {timelineData.map((item, index) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  index={index}
                  isLeft={index % 2 === 0}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
